// context/JobsContext.jsx - GraphQL Integrated Version
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from './AuthContext';

// GraphQL Queries
const GET_AVAILABLE_JOBS = gql`
  query GetAvailableJobs($category: String) {
    availableJobs(category: $category) {
      id
      title
      description
      type
      tokenReward
      status
      location {
        address
        city
        state
      }
      schedule {
        date
        startTime
        endTime
        flexible
        deadline
      }
      requirements {
        minTrustScore
        requiredSkills {
          skillId
          name
          tier
        }
        preferredSkills {
          skillId
          name
          tier
        }
      }
      
      groupId
      isPlatformJob
      createdAt
    }
  }
`;

const GET_MY_JOBS = gql`
  query GetMyJobs {
    myJobs {
      id
      title
      description
      type
      tokenReward
      status
      location {
        address
        city
        state
      }
      schedule {
        date
        startTime
        endTime
        flexible
        deadline
      }
      requirements {
        minTrustScore
        requiredSkills {
          skillId
          name
          tier
        }
        
      }
      
      groupId
      isPlatformJob
      claimedAt
      completedAt
      completionData {
        notes
        itemsProcessed
        stopsCompleted
      }
      approvalData {
        rating
        feedback
        tokensAwarded
        approvedAt
      }
    }
  }
`;

const GET_MY_COMPLETED_JOBS = gql`
  query GetMyCompletedJobs {
    myJobs(status: COMPLETED) {
      id
      title
      description
      type
      tokenReward
      bonusTokens
      status
      groupId
      claimedAt
      completedAt
      completionData {
        notes
        itemsProcessed
        stopsCompleted
      }
      approvalData {
        rating
        feedback
        tokensAwarded
        approvedAt
      }
    }
  }
`;

const CLAIM_JOB = gql`
  mutation ClaimJob($jobId: ID!) {
    claimJob(jobId: $jobId) {
      id
      status
      claimedAt
    }
  }
`;

const SUBMIT_JOB_COMPLETION = gql`
  mutation SubmitJobCompletion($jobId: ID!, $notes: String, $deliveryPhotoUrl: String!, $selfieUrl: String) {
    submitJobCompletion(jobId: $jobId, notes: $notes, deliveryPhotoUrl: $deliveryPhotoUrl, selfieUrl: $selfieUrl) {
      id
      status
      completion {
        completedAt
        workerNotes
      }
    }
  }
`;

export const JobsContext = createContext({
  availableJobs: [],
  myJobs: [],
  completedJobs: [],
  opportunities: [],
  allJobs: [],
  loading: false,
  error: null,
  filters: {
    showQualified: true,
    showOpportunities: true,
    category: 'all',
    sortBy: 'matchScore'
  },
  // Methods
  claimJob: () => {},
  completeJob: () => {},
  getJobMatchScore: () => {},
  refreshJobs: () => {},
  setFilters: () => {},
  getJobById: () => {}
});

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within JobsProvider');
  }
  return context;
};

export const JobsProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  
  const [filters, setFilters] = useState({
    showQualified: true,
    showOpportunities: true,
    category: 'all',
    sortBy: 'matchScore'
  });

  // GraphQL Queries
  const { 
    data: availableData, 
    loading: availableLoading, 
    error: availableError,
    refetch: refetchAvailable 
  } = useQuery(GET_AVAILABLE_JOBS, {
    variables: { category: filters.category !== 'all' ? filters.category : null },
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });

  const { 
    data: myJobsData, 
    loading: myJobsLoading, 
    error: myJobsError,
    refetch: refetchMyJobs 
  } = useQuery(GET_MY_JOBS, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });

  const { 
    data: completedData, 
    loading: completedLoading,
    refetch: refetchCompleted 
  } = useQuery(GET_MY_COMPLETED_JOBS, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });

  // GraphQL Mutations
  const [claimJobMutation] = useMutation(CLAIM_JOB, {
    refetchQueries: [
      { query: GET_AVAILABLE_JOBS },
      { query: GET_MY_JOBS }
    ]
  });

  const [submitCompletionMutation] = useMutation(SUBMIT_JOB_COMPLETION, {
    refetchQueries: [
      { query: GET_MY_JOBS },
      { query: GET_MY_COMPLETED_JOBS }
    ]
  });

  // Process available jobs - add match scoring
  const processedAvailableJobs = React.useMemo(() => {
    const jobs = availableData?.availableJobs || [];
    
    return jobs.map(job => {
      // Calculate match score based on user's skills (simplified for now)
      // In production, this could be calculated server-side
      const matchScore = 100; // Assume qualified since backend filters
      
      return {
        ...job,
        matchScore,
        qualified: true,
        meetsAllRequirements: true,
        meetsTrustRequirement: true,
        missingSkills: [],
        matchingSkills: job.requiredSkills || [],
        location: job.location?.city 
          ? `${job.location.city}, ${job.location.state}` 
          : 'Remote',
        groupTitle: job.isPlatformJob ? 'Platform Job' : (job.groupId ? `Group ${job.groupId}` : null)
      };
    }).sort((a, b) => {
      switch (filters.sortBy) {
        case 'tokenReward':
          return b.tokenReward - a.tokenReward;
        case 'deadline':
          return new Date(a.schedule?.deadline || a.schedule?.date) - 
                 new Date(b.schedule?.deadline || b.schedule?.date);
        default: // matchScore
          return b.matchScore - a.matchScore;
      }
    });
  }, [availableData, filters.sortBy]);

  // Process my jobs (in progress)
  const processedMyJobs = React.useMemo(() => {
    const jobs = myJobsData?.myJobs || [];
    
    return jobs.map(job => ({
      ...job,
      location: job.location?.city 
        ? `${job.location.city}, ${job.location.state}` 
        : 'Remote',
      groupTitle: job.isPlatformJob ? 'Platform Job' : (job.groupId ? `Group ${job.groupId}` : null)
    }));
  }, [myJobsData]);

  // Process completed jobs
  const processedCompletedJobs = React.useMemo(() => {
    const jobs = completedData?.myCompletedJobs || [];
    
    return jobs.map(job => ({
      ...job,
      completedAt: job.completion?.completedAt || job.approval?.approvedAt,
      approvalData: job.approval
    }));
  }, [completedData]);

  // Claim a job
  const claimJob = useCallback(async (jobId) => {
    try {
      const { data } = await claimJobMutation({
        variables: { jobId }
      });
      
      return data.claimJob;
    } catch (err) {
      console.error('Error claiming job:', err);
      throw new Error(err.message || 'Failed to claim job');
    }
  }, [claimJobMutation]);

  // Complete a job
  const completeJob = useCallback(async (jobId, completionData = {}) => {
    try {
      // For now, use placeholder photo URLs
      // In production, these would come from S3 upload
      const deliveryPhotoUrl = completionData.deliveryPhotoUrl || 'https://placeholder.com/delivery.jpg';
      const selfieUrl = completionData.selfieUrl || null;
      
      const { data } = await submitCompletionMutation({
        variables: {
          jobId,
          notes: completionData.comments || completionData.workerComments || '',
          deliveryPhotoUrl,
          selfieUrl
        }
      });
      
      return data.submitJobCompletion;
    } catch (err) {
      console.error('Error completing job:', err);
      throw new Error(err.message || 'Failed to submit job completion');
    }
  }, [submitCompletionMutation]);

  // Get match score for a job (simplified - backend handles filtering)
  const getJobMatchScore = useCallback((jobId) => {
    const job = processedAvailableJobs.find(j => j.id === jobId) ||
                processedMyJobs.find(j => j.id === jobId);
    
    if (!job) return null;
    
    return {
      matchScore: job.matchScore || 100,
      qualified: true,
      meetsAllRequirements: true,
      meetsTrustRequirement: true,
      missingSkills: [],
      matchingSkills: job.requiredSkills || []
    };
  }, [processedAvailableJobs, processedMyJobs]);

  // Refresh all jobs
  const refreshJobs = useCallback(() => {
    refetchAvailable();
    refetchMyJobs();
    refetchCompleted();
  }, [refetchAvailable, refetchMyJobs, refetchCompleted]);

  // Get job by ID
  const getJobById = useCallback((jobId) => {
    return processedAvailableJobs.find(j => j.id === jobId) ||
           processedMyJobs.find(j => j.id === jobId) ||
           processedCompletedJobs.find(j => j.id === jobId);
  }, [processedAvailableJobs, processedMyJobs, processedCompletedJobs]);

  // Combined loading state
  const loading = availableLoading || myJobsLoading || completedLoading;
  
  // Combined error state
  const error = availableError || myJobsError;

  const value = {
    availableJobs: processedAvailableJobs,
    myJobs: processedMyJobs,
    completedJobs: processedCompletedJobs,
    opportunities: [], // Backend filters, so opportunities handled differently
    allJobs: [...processedAvailableJobs, ...processedMyJobs],
    loading,
    error: error?.message || null,
    filters,
    claimJob,
    completeJob,
    getJobMatchScore,
    refreshJobs,
    setFilters,
    getJobById
  };

  return (
    <JobsContext.Provider value={value}>
      {children}
    </JobsContext.Provider>
  );
};